import type { ErrorEnvelope, ErrorCode, ResponseMeta } from '@geotracker/contracts';

/**
 * Typed API client.
 *
 * Errors from the API always arrive in the standard envelope with a stable
 * `error.code`. This maps them to a typed exception so UI code branches on the
 * code — never on the message, which is localized and reworded freely.
 */
export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | string,
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
    /** Field-keyed, present on 422 — feeds straight into react-hook-form. */
    readonly fieldErrors?: Record<string, string[]>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isValidation(): boolean {
    return this.status === 422;
  }

  get isAuth(): boolean {
    return this.status === 401;
  }
}

interface SuccessBody<T> {
  success: true;
  message: string;
  data: T;
  meta: ResponseMeta;
}

export interface ApiResult<T> {
  data: T;
  message: string;
  meta: ResponseMeta;
}

const BASE = '/api/v1';

/**
 * Access token lives in memory only — never localStorage, which any injected
 * script can read.
 */
let accessToken: string | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

export const getAccessToken = (): string | null => accessToken;

/**
 * The refresh token, in sessionStorage.
 *
 * KNOWN GAP, deliberately visible rather than hidden: sessionStorage is
 * XSS-readable. The correct fix is for the API to set the refresh token as an
 * httpOnly + SameSite=Strict cookie scoped to /auth/refresh — the design in
 * docs/03-API-SPECIFICATION.md §6.2 already specifies exactly that, and the
 * server does not yet implement it.
 *
 * An earlier version of this file assumed that cookie already existed and sent
 * a refresh request with no body at all. The server's RefreshRequestSchema
 * requires `refreshToken`, so every refresh failed — with a 500, because
 * Fastify rejects an empty body under `content-type: application/json` before
 * validation ever runs. The read below is what makes the request valid.
 */
const REFRESH_KEY = 'gt-refresh';

export const getRefreshToken = (): string | null => sessionStorage.getItem(REFRESH_KEY);

export const setRefreshToken = (token: string | null): void => {
  if (token === null) sessionStorage.removeItem(REFRESH_KEY);
  else sessionStorage.setItem(REFRESH_KEY, token);
};

export const clearSession = (): void => {
  setAccessToken(null);
  setRefreshToken(null);
};

/**
 * Single-flight refresh.
 *
 * Refresh tokens are single-use and rotate; presenting a spent one is treated
 * by the server as a stolen-token replay and revokes the ENTIRE token family.
 * So two refreshes must never be in flight at once, and this is not a
 * theoretical concern:
 *
 *  • ten concurrent requests that all 401 would each try to refresh;
 *  • React StrictMode double-invokes effects in development, so the session
 *    restore on startup fired twice, spent the same token twice, and signed the
 *    admin out of every session — on every page load.
 *
 * Both collapse onto this one promise. Callers that arrive while a refresh is
 * running await the same result rather than starting another.
 */
let refreshInFlight: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;

    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) {
        // 401/403 is definitive: the token is spent, expired or revoked, and
        // only a re-login fixes it. A 5xx or a network failure is not — keeping
        // the token means a blip does not force everyone to sign in again.
        if (res.status === 401 || res.status === 403) clearSession();
        return false;
      }

      const body = (await res.json()) as SuccessBody<{
        accessToken: string;
        refreshToken: string;
      }>;
      setAccessToken(body.data.accessToken);
      // Storing the ROTATED token is not optional: the one just presented is
      // now spent server-side, so keeping it would guarantee a reuse-detection
      // on the next attempt.
      setRefreshToken(body.data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so callers awaiting this promise all observe
      // the same result before a new attempt can start.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();
  return refreshInFlight;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Internal: prevents infinite recursion after a refresh attempt. */
  _retried?: boolean;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
  const { body, _retried, headers, ...rest } = options;

  const response = await fetch(`${BASE}${path}`, {
    ...rest,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (response.status === 401 && !_retried) {
    if (await refreshAccessToken()) {
      return apiRequest<T>(path, { ...options, _retried: true });
    }
  }

  const text = await response.text();
  const parsed: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const envelope = parsed as ErrorEnvelope | null;
    throw new ApiError(
      envelope?.error?.code ?? 'INTERNAL_ERROR',
      envelope?.message ?? `Request failed with status ${response.status}`,
      response.status,
      envelope?.error?.details,
      envelope?.errors,
      envelope?.meta?.requestId,
    );
  }

  const success = parsed as SuccessBody<T>;
  return { data: success.data, message: success.message, meta: success.meta };
}

/**
 * Fetches a binary resource (audio, an export) as a Blob, with the bearer token.
 *
 * The reason this exists rather than an `<audio src>` or an `<img src>`: those
 * cannot carry an Authorization header, and this API deliberately keeps its
 * token out of cookies and query strings, so the browser has no other way to
 * authenticate a media request. The caller turns the blob into an object URL.
 *
 * Shares the single-flight refresh with `apiRequest` — a 401 here refreshes and
 * retries once, exactly as a JSON request would, so a token expiring mid-listen
 * does not silently 401 the next segment.
 */
export async function apiBlob(path: string, _retried = false): Promise<Blob> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
  });

  if (response.status === 401 && !_retried) {
    if (await refreshAccessToken()) return apiBlob(path, true);
  }

  if (!response.ok) {
    throw new ApiError(
      'MEDIA_ERROR',
      `Could not load media (${response.status})`,
      response.status,
    );
  }
  return response.blob();
}

export const api = {
  get: <T>(path: string, init?: RequestOptions) => apiRequest<T>(path, { ...init, method: 'GET' }),
  post: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: 'PUT', body }),
  delete: <T>(path: string, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: 'DELETE' }),
};
